import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faChevronDown, faMagnifyingGlass, faSort, faSortDown, faSortUp, faTableList } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useMemo, useState } from "react"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import InputField from "../../../components/InputField/InputField"
import type { FinancialSortDirection, FinancialTableColumn } from "../types/financialUi"
import { normalizeFinancialSearch, paginateFinancialRows, sortFinancialRows } from "../utils/financialTableUtils"
import { FinancialEmptyState } from "./FinancialEmptyState"
import { FinancialPagination } from "./FinancialPagination"

interface FinancialDataTableProps<T> {
  title: string
  subtitle?: string
  rows: readonly T[]
  getRowKey: (row: T) => React.Key
  columns: readonly FinancialTableColumn<T>[]
  searchText: (row: T) => string
  searchLabel: string
  emptyTitle: string
  emptyDescription?: string
  icon?: IconProp
  headerLayout?: "responsive" | "stacked"
  initialSort?: { columnId: string; direction: FinancialSortDirection }
  initialPageSize?: number
  pageSizeOptions?: readonly number[]
  paginationLabels: {
    ariaLabel: string
    previous: string
    next: string
    page: (current: number, total: number) => string
    range: (first: number, last: number, total: number) => string
    itemsPerPage: string
  }
}

export const FinancialDataTable = <T,>({
  title,
  subtitle,
  rows,
  getRowKey,
  columns,
  searchText,
  searchLabel,
  emptyTitle,
  emptyDescription,
  icon = faTableList,
  headerLayout = "responsive",
  initialSort,
  initialPageSize = 10,
  pageSizeOptions = [10, 25, 50],
  paginationLabels,
}: FinancialDataTableProps<T>) => {
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sort, setSort] = useState(initialSort)

  const filteredRows = useMemo(() => {
    const normalizedSearch = normalizeFinancialSearch(search)
    if (!normalizedSearch) return [...rows]

    return rows.filter((row) => normalizeFinancialSearch(searchText(row)).includes(normalizedSearch))
  }, [rows, search, searchText])

  const sortedRows = useMemo(() => {
    if (!sort) return filteredRows
    const column = columns.find((item) => item.id === sort.columnId)
    return column?.sortValue ? sortFinancialRows(filteredRows, column.sortValue, sort.direction) : filteredRows
  }, [columns, filteredRows, sort])

  const pagination = useMemo(() => paginateFinancialRows(sortedRows, page, pageSize), [page, pageSize, sortedRows])

  const handleSort = (column: FinancialTableColumn<T>) => {
    if (!column.sortValue) return

    setSort((current) => ({
      columnId: column.id,
      direction:
        current?.columnId === column.id && current.direction === "ascending" ? "descending" : "ascending",
    }))
    setPage(1)
  }

  const primaryColumn = columns.find((column) => column.mobilePriority === "primary") ?? columns[0]
  const summaryColumns = columns.filter((column) => column.mobilePriority === "summary")
  const detailColumns = columns.filter((column) => column !== primaryColumn && column.mobilePriority !== "summary")
  const hasStackedHeader = headerLayout === "stacked"
  const tableMinWidthClassName = columns.length <= 3 ? "min-w-[420px]" : "min-w-[760px]"

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="0.55s">
      <section className="group min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition duration-300 hover:border-orange-200 hover:shadow-md motion-reduce:transition-none md:p-5 xl:p-6">
        <div className={`flex flex-col gap-4 ${hasStackedHeader ? "" : "xl:flex-row xl:items-end xl:justify-between"}`}>
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600 shadow-sm transition-transform duration-300 group-hover:scale-105 motion-reduce:transition-none">
              <FontAwesomeIcon icon={icon} />
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-black leading-tight text-slate-950 xl:text-xl">{title}</h2>
              {subtitle && <p className="mt-1 text-sm font-medium leading-5 text-slate-500 xl:text-base">{subtitle}</p>}
            </div>
          </div>
          <div className={`w-full ${hasStackedHeader ? "" : "xl:max-w-md"}`}>
            <InputField
              type="text"
              value={search}
              icon={faMagnifyingGlass}
              placeholder={searchLabel}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              descriptionKey="interactionDescriptions:pages.cashFlow.financialDataTable.input.search"
            />
          </div>
        </div>

        {pagination.rows.length === 0 ? (
          <div className="mt-4">
            <FinancialEmptyState title={emptyTitle} description={emptyDescription} compact />
          </div>
        ) : (
          <>
          <div className="mt-4 hidden overflow-x-auto md:block">
            <table className={`w-full ${tableMinWidthClassName} border-separate border-spacing-0 text-sm`}>
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr>
                  {columns.map((column) => {
                    const activeSort = sort?.columnId === column.id ? sort.direction : undefined
                    return (
                      <th
                        key={column.id}
                        scope="col"
                        aria-sort={activeSort ?? "none"}
                        className={`border-y border-slate-200 px-3 py-3 text-xs font-extrabold uppercase tracking-wide text-slate-600 first:rounded-l-xl first:border-l last:rounded-r-xl last:border-r ${column.align === "right" ? "text-right" : "text-left"}`}
                      >
                        {column.sortValue ? (
                          <button
                            type="button"
                            className={`inline-flex items-center gap-2 ${column.align === "right" ? "ml-auto" : ""}`}
                            onClick={() => handleSort(column)}
                            data-interaction-description-key="interactionDescriptions:pages.cashFlow.financialDataTable.button.sortColumn"
                          >
                            <span>{column.label}</span>
                            <FontAwesomeIcon icon={activeSort === "ascending" ? faSortUp : activeSort === "descending" ? faSortDown : faSort} />
                          </button>
                        ) : (
                          column.label
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {pagination.rows.map((row) => (
                  <tr key={getRowKey(row)} className="border-b border-slate-100 even:bg-slate-50/60 hover:bg-orange-50/40">
                    {columns.map((column) => (
                      <td
                        key={column.id}
                        className={`border-b border-slate-100 px-3 py-3 text-slate-700 ${column.align === "right" ? "text-right tabular-nums" : "text-left"}`}
                      >
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 space-y-3 md:hidden">
            {pagination.rows.map((row) => (
              <details key={getRowKey(row)} className="group rounded-xl border border-slate-200 bg-white shadow-sm">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500">
                  <div className="min-w-0 flex-1">
                    <div className="font-extrabold text-slate-900">{primaryColumn?.render(row)}</div>
                    {summaryColumns.length > 0 && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {summaryColumns.map((column) => (
                          <div key={column.id} className="min-w-0">
                            <span className="block text-[10px] font-extrabold uppercase tracking-wide text-slate-400">{column.label}</span>
                            <span className="mt-0.5 block truncate text-xs font-bold text-slate-700">{column.render(row)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <FontAwesomeIcon icon={faChevronDown} className="text-slate-400 transition group-open:rotate-180" />
                </summary>
                <dl className="grid grid-cols-1 gap-3 border-t border-slate-100 bg-slate-50/70 p-4 sm:grid-cols-2">
                  {detailColumns.map((column) => (
                    <div key={column.id}>
                      <dt className="text-[10px] font-extrabold uppercase tracking-wide text-slate-400">{column.label}</dt>
                      <dd className="mt-1 text-sm font-bold text-slate-700">{column.render(row)}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            ))}
          </div>

          <div className="mt-4">
            <FinancialPagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              firstItem={pagination.firstIndex + 1}
              lastItem={pagination.lastIndex}
              totalItems={sortedRows.length}
              labels={paginationLabels}
              onPageChange={setPage}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize)
                setPage(1)
              }}
            />
          </div>
          </>
        )}
      </section>
    </Animate>
  )
}
